import { z } from "zod";

import { containsEmail, rulesMet } from "@/utils/passwordRules.ts";
import { PERSON_NAME_MAX_LENGTH, PERSON_NAME_PATTERN } from "@/utils/validation.ts";

const personName = z
	.string()
	.trim()
	.min(1, { message: "validation.tooSmall" })
	.max(PERSON_NAME_MAX_LENGTH, { message: "validation.nameTooLong" })
	.regex(PERSON_NAME_PATTERN, { message: "validation.invalidString" });

export const Step1Schema = z.object({
	firstName: personName,
	lastName: personName,
	email: z.string().min(1, { message: "validation.tooSmall" }).email("validation.emailInvalid"),
	agbAccepted: z.boolean().refine((val) => val, {
		message: "validation.agbUnchecked"
	})
});

export const Step2Schema = z.object({
	code: z.array(z.string()).length(6)
});

/**
 * The password rules depend on the e-mail address entered in step 1
 * (the password must not contain it), hence the schema is created per e-mail.
 */
export const createStep3Schema = (email: string) =>
	z
		.object({
			password: z.string(),
			confirmPassword: z.string()
		})
		.refine((data) => rulesMet(data.password), {
			message: "validation.passwordInvalid",
			path: ["password"]
		})
		.refine((data) => !containsEmail(data.password, email), {
			message: "validation.passwordContainsEmail",
			path: ["password"]
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: "validation.passwordsDontMatch",
			path: ["confirmPassword"]
		});

export type Step1FormData = z.infer<typeof Step1Schema>;
export type Step2FormData = z.infer<typeof Step2Schema>;
export type Step3FormData = z.infer<ReturnType<typeof createStep3Schema>>;
